import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { setNext } from "@/lib/draft";
import { useAuth, useSettings } from "@/context/AppContext";

export const UfSelect = ({ value, onChange, testid = "uf-select", className = "field" }) => {
  const { settings } = useSettings() || {};
  return (
    <select className={className} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Selecione o estado</option>
      {(settings?.ufs || []).map((u) => <option key={u} value={u}>{u}</option>)}
    </select>
  );
};

export const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
export const phoneOk = (v) => [10, 11].includes(v.replace(/\D/g, "").length);
export const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

const GoogleButton = ({ returnPath }) => (
  <button
    type="button"
    className="btn-secondary w-full"
    data-testid="auth-google-btn"
    onClick={() => {
      setNext(returnPath);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/minhas-solicitacoes";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
  >
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    Continuar com Google
  </button>
);

export function AuthPanel({ returnPath = "/minhas-solicitacoes", onDone, initialTab = "register" }) {
  const { setUser } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [f, setF] = useState({ name: "", email: "", password: "", state: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const up = (k) => (e) => {
    setErr("");
    setF({ ...f, [k]: k === "phone" ? maskPhone(e.target.value) : e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!emailOk(f.email)) return setErr("Informe um e-mail válido.");
    if (tab === "register") {
      if (f.name.trim().split(" ").length < 2) return setErr("Informe seu nome completo.");
      if (!f.state) return setErr("Selecione seu estado.");
      if (!phoneOk(f.phone)) return setErr("Informe um celular válido com DDD.");
      if (f.password.length < 8) return setErr("A senha deve ter ao menos 8 caracteres.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(tab === "register" ? "/auth/register" : "/auth/login",
        tab === "register" ? f : { email: f.email, password: f.password });
      if (data.mfa_required) {
        setErr("Conta administrativa: use o acesso em /admin/login.");
        return;
      }
      setUser(data);
      toast.success(tab === "register" ? "Cadastro realizado" : "Você entrou na sua conta");
      onDone?.(data);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!emailOk(f.email)) return setErr("Informe seu e-mail acima para recuperar a senha.");
    await api.post("/auth/forgot-password", { email: f.email }).catch(() => {});
    setForgotSent(true);
  };

  return (
    <div data-testid="auth-panel">
      <div className="mb-5 grid grid-cols-2 rounded-xl bg-[#EDF5FF] p-1">
        {[["register", "Criar cadastro"], ["login", "Já tenho conta"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setErr(""); }} data-testid={`auth-tab-${k}`}
            className={`rounded-lg py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-white text-[#1465C8] shadow-sm" : "text-[#64748B]"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        {tab === "register" && (
          <>
            <div><label className="label">Nome completo</label><input className="field" value={f.name} onChange={up("name")} autoComplete="name" data-testid="auth-name-input" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label">Estado de residência</label><UfSelect value={f.state} onChange={(v) => setF({ ...f, state: v })} testid="auth-state-select" /></div>
              <div><label className="label">Celular com DDD</label><input className="field" inputMode="tel" placeholder="(11) 91234-5678" value={f.phone} onChange={up("phone")} autoComplete="tel" data-testid="auth-phone-input" /></div>
            </div>
          </>
        )}
        <div><label className="label">E-mail</label><input className="field" type="email" value={f.email} onChange={up("email")} autoComplete="email" data-testid="auth-email-input" /></div>
        <div><label className="label">Senha</label><input className="field" type="password" value={f.password} onChange={up("password")} autoComplete={tab === "register" ? "new-password" : "current-password"} data-testid="auth-password-input" />
          {tab === "register" && <p className="mt-1 text-xs text-[#64748B]">Mínimo de 8 caracteres.</p>}
        </div>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-error">{err}</p>}
        {forgotSent && <p className="rounded-lg bg-[#EDF5FF] px-3 py-2 text-sm" data-testid="auth-forgot-sent">Se o e-mail estiver cadastrado, você receberá um link de redefinição.</p>}
        <button className="btn-primary w-full" disabled={busy} data-testid="auth-submit-btn">{busy ? "Aguarde..." : tab === "register" ? "Cadastrar e continuar" : "Entrar e continuar"}</button>
        {tab === "login" && <button type="button" className="btn-ghost w-full justify-center" onClick={forgot} data-testid="auth-forgot-btn">Esqueci minha senha</button>}
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-[#64748B]"><span className="h-px flex-1 bg-slate-200" />ou<span className="h-px flex-1 bg-slate-200" /></div>
      <GoogleButton returnPath={returnPath} />
    </div>
  );
}
